/**
 * Wording that is not always identical. Real crews say the same thing several ways, and a frequency
 * where every pilot reads back in exactly the same words is what gives a simulator away.
 *
 * `phraseology.json` holds weighted templates per kind of call over the typed phrase parts; index 0
 * is what the game has always said. `RealismOptions.phraseVariation` is the chance of using one of
 * the others. Snapshot tests explicitly select zero to pin the canonical wording.
 */
import table from './phraseology.json' with { type: 'json' };
import type { PhrasePart } from './brain/phrase';
import type { Rng } from './rng';

type Variants = Record<string, [string, number][]>;
const VARIANTS = table as unknown as Variants;

export type PhraseKind = Exclude<keyof typeof table, '_comment'>;
/** A placeholder stands for one typed part, several, or nothing at all. */
export type PhraseVars = Record<string, PhrasePart | PhrasePart[] | undefined>;

/**
 * How a crew talks. A terse crew reaches for the shortest wording, a chatty one for the longest,
 * and both leave the canonical words more often than a standard crew does. Fixed per flight, so
 * one aircraft keeps its habits while the frequency as a whole sounds like different people.
 */
export type CrewStyle = 'terse' | 'chatty';
export const CREW_STYLES: readonly (CrewStyle | undefined)[] = [undefined, undefined, 'terse', 'terse', 'chatty', 'chatty', undefined, 'terse', 'chatty', undefined];
/** How much more often a crew with a style leaves the canonical wording; nothing varies at zero. */
const STYLE_VARIATION = 1.75;

const styleWeight = (text: string, weight: number, style: CrewStyle) => {
  const words = text.split(/\s+/).length;
  return style === 'terse' ? weight / (words * words) : weight * words * words;
};

/**
 * `extras` are an airport's own alternatives (`x_atc.phraseExtras`, e.g. "shalom" at LLBG). They join
 * the alternatives only, so canonical wording never depends on the airport.
 */
export function variantOf(kind: PhraseKind, rng?: Rng, variation = 0, extras: readonly [string, number][] = [], style?: CrewStyle): string {
  const options = [...(VARIANTS[kind] ?? []), ...extras];
  if (!options?.length) return '';
  const canonical = options[0]![0];
  const chance = style ? Math.min(1, variation * STYLE_VARIATION) : variation;
  if (!rng || chance <= 0 || options.length === 1 || rng.next() >= chance) return canonical;
  // Among the alternatives only: the canonical wording already had its chance. A crew's style
  // leans the choice towards its own kind of wording.
  const rest = style ? options.slice(1).map(([text, weight]) => [text, styleWeight(text, weight, style)] as [string, number]) : options.slice(1);
  const total = rest.reduce((sum, [, weight]) => sum + weight, 0);
  let r = rng.next() * total;
  for (const [text, weight] of rest) { r -= weight; if (r <= 0) return text; }
  return canonical;
}

/** Fill a template's placeholders with parts; the words between them become plain-words parts. */
export function partsFromTemplate(template: string, vars: PhraseVars = {}): PhrasePart[] {
  const out: PhrasePart[] = [];
  for (const piece of template.split(/(\{[a-zA-Z]+\})/)) {
    if (!piece) continue;
    const name = /^\{([a-zA-Z]+)\}$/.exec(piece)?.[1];
    if (name === undefined) { const v = piece.trim() ? { t: 'words' as const, v: piece.trim() } : null; if (v) out.push(v); continue; }
    const value = vars[name];
    if (!value) continue;
    out.push(...(Array.isArray(value) ? value : [value]));
  }
  return out;
}

/** The wording for one call, ready to speak. */
export function phraseParts(kind: PhraseKind, vars: PhraseVars = {}, rng?: Rng, variation = 0, extras?: readonly [string, number][], style?: CrewStyle): PhrasePart[] {
  return partsFromTemplate(variantOf(kind, rng, variation, extras, style), vars);
}

/** Time-of-day greeting kind for a local hour. */
export function greetingKind(hour: number): PhraseKind {
  return hour >= 4 && hour < 12 ? 'greetingMorning' : hour >= 12 && hour < 18 ? 'greetingAfternoon' : 'greetingEvening';
}

/** Every template, for tests and as the style guide handed to a model brain. */
export function allVariants(): Variants {
  const out: Variants = {};
  for (const [kind, options] of Object.entries(VARIANTS)) if (kind !== '_comment') out[kind] = options;
  return out;
}
